"use client";

import Link from "next/link";
import { useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { DZTypes } from "@dealerzone/libs";

interface NavLink {
  href: string;
  label: string;
}

interface MobileMenuProps {
  dealer: DZTypes.Dealer;
  inventoryLinks: NavLink[];
  pageLinks: NavLink[];
}

export default function MobileMenu({ dealer, inventoryLinks, pageLinks }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inventoryExpanded, setInventoryExpanded] = useState(false);
  const [pagesExpanded, setPagesExpanded] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden p-2 text-zinc-700 hover:text-red-600"
        aria-label="Open menu"
      >
        <i className="bootstrap-list text-2xl"></i>
      </button>

      <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50 lg:hidden">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <DialogPanel className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-xl">
          <div className="flex items-center justify-between p-4 border-b border-zinc-200">
            <span className="text-lg font-semibold text-zinc-900">{dealer.name}</span>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-zinc-500 hover:text-zinc-700"
              aria-label="Close menu"
            >
              <i className="bootstrap-x-lg text-xl"></i>
            </button>
          </div>

          <div className="p-4">
            <nav className="space-y-1">
              <div>
                <button
                  onClick={() => setInventoryExpanded(!inventoryExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-zinc-700 hover:bg-zinc-100 rounded-lg font-medium"
                >
                  <span>Inventory</span>
                  <i className={`bootstrap-chevron-down text-sm transition-transform ${inventoryExpanded ? "rotate-180" : ""}`}></i>
                </button>
                {inventoryExpanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {inventoryLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                href="/repaired-vehicles"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 text-zinc-700 hover:bg-zinc-100 rounded-lg font-medium"
              >
                Ready To Go
              </Link>

              <Link
                href="/lowered-price"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 text-zinc-700 hover:bg-zinc-100 rounded-lg font-medium"
              >
                Priced Down
              </Link>

              <div>
                <button
                  onClick={() => setPagesExpanded(!pagesExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-zinc-700 hover:bg-zinc-100 rounded-lg font-medium"
                >
                  <span>Pages</span>
                  <i className={`bootstrap-chevron-down text-sm transition-transform ${pagesExpanded ? "rotate-180" : ""}`}></i>
                </button>
                {pagesExpanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {pageLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                href="/contact"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 text-zinc-700 hover:bg-zinc-100 rounded-lg font-medium"
              >
                Contact Us
              </Link>
            </nav>

            <div className="mt-6 pt-6 border-t border-zinc-200 space-y-3">
              <a
                href="tel:+18553265681"
                className="flex items-center gap-3 px-4 py-2 text-zinc-700 hover:text-red-600"
              >
                <i className="bootstrap-telephone text-red-500"></i>
                <span>855-326-5681</span>
              </a>
              <a
                href="tel:+13107034199"
                className="flex items-center gap-3 px-4 py-2 text-zinc-700 hover:text-red-600"
              >
                <i className="bootstrap-telephone text-red-500"></i>
                <span>310-703-4199</span>
              </a>
              <a
                href="mailto:sales@getsmc.com"
                className="flex items-center gap-3 px-4 py-2 text-zinc-700 hover:text-red-600"
              >
                <i className="bootstrap-envelope text-red-500"></i>
                <span>sales@getsmc.com</span>
              </a>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
}
